import axios from 'axios';
import type { AxiosError } from 'axios';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import type {
  TranslationLanguage,
  TranslationLanguageListResponse,
  TranslationLanguagePutRequest,
  TranslationListResponse,
  TranslationItem,
  TranslationPutRequest,
  TranslationGetResponse,
  TranslationPutResponse,
  TranslationImportRequest,
  TranslationImportResponse,
  TranslationGenerateRequest,
  TranslationGenerateResponse,
} from '@/types/translations';

type ApiOptions = {
  tenantId?: string;
};

type ErrorPayload = {
  message?: string;
  error?: string;
  details?: unknown;
};

export class TranslationsApiError extends Error {
  status: number | null;
  details: string[];

  constructor(message: string, status: number | null, details: string[] = []) {
    super(message);
    this.name = 'TranslationsApiError';
    this.status = status;
    this.details = details;
  }
}

const is_record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const build_headers = (options: ApiOptions = {}) => {
  const context = resolveSaasContext({ tenantId: options.tenantId });
  return buildSaasHeaders(context);
};

const normalize_string = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const normalize_nullable_string = (value: unknown) => {
  const normalized = normalize_string(value);
  return normalized.length > 0 ? normalized : null;
};

const normalize_boolean = (value: unknown) =>
  value === true;

const normalize_string_record = (value: unknown): Record<string, string> => {
  if (!is_record(value)) return {};
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val === 'string') {
      result[key] = val;
    }
  }
  return result;
};

const normalize_language = (value: unknown): TranslationLanguage => {
  const payload = is_record(value) ? value : {};
  return {
    locale: normalize_string(payload.locale),
    label: normalize_string(payload.label),
    enabled: normalize_boolean(payload.enabled),
    defaultLanguage: normalize_boolean(payload.defaultLanguage),
    description: normalize_nullable_string(payload.description),
  };
};

const normalize_item = (value: unknown): TranslationItem => {
  const payload = is_record(value) ? value : {};
  return {
    namespace: normalize_string(payload.namespace),
    key: normalize_string(payload.key),
    values: normalize_string_record(payload.values),
    description: normalize_nullable_string(payload.description),
  };
};

const to_translations_api_error = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorPayload>;
    const status = axiosError.response?.status ?? null;
    const payload = axiosError.response?.data;
    const details = Array.isArray(payload?.details)
      ? payload.details.filter((d): d is string => typeof d === 'string')
      : [];
    return new TranslationsApiError(
      payload?.message ?? axiosError.message ?? fallbackMessage,
      status,
      details,
    );
  }

  if (error instanceof Error) {
    return new TranslationsApiError(error.message, null);
  }

  return new TranslationsApiError(fallbackMessage, null);
};

// ─── Language Endpoints ────────────────────────────────────────────────────

export async function listLanguages(
  workspaceId: string,
  options: ApiOptions = {},
): Promise<TranslationLanguageListResponse> {
  try {
    const response = await axios.get<unknown>('/api/v1/translations/languages', {
      headers: build_headers(options),
      params: { workspaceId },
    });
    const payload = is_record(response.data) ? response.data : {};
    return {
      languages: Array.isArray(payload.languages)
        ? payload.languages.map(normalize_language)
        : [],
    };
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to load translation languages.');
  }
}

export async function putLanguage(
  locale: string,
  workspaceId: string,
  body: TranslationLanguagePutRequest,
  options: ApiOptions = {},
): Promise<TranslationLanguage> {
  try {
    const response = await axios.put<unknown>(
      `/api/v1/translations/languages/${encodeURIComponent(locale)}`,
      {
        label: body.label,
        enabled: body.enabled,
        defaultLanguage: body.defaultLanguage,
        description: body.description,
      },
      {
        headers: build_headers(options),
        params: { workspaceId },
      },
    );
    return normalize_language(response.data);
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to save translation language.');
  }
}

export async function deleteLanguage(
  locale: string,
  workspaceId: string,
  options: ApiOptions = {},
): Promise<void> {
  try {
    await axios.delete(
      `/api/v1/translations/languages/${encodeURIComponent(locale)}`,
      {
        headers: build_headers(options),
        params: { workspaceId },
      },
    );
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to delete translation language.');
  }
}

// ─── Translation Key Endpoints ────────────────────────────────────────────

export async function listTranslations(
  workspaceId: string,
  params?: { namespace?: string; q?: string },
  options: ApiOptions = {},
): Promise<TranslationListResponse> {
  try {
    const response = await axios.get<unknown>('/api/v1/translations', {
      headers: build_headers(options),
      params: { workspaceId, ...params },
    });
    const payload = is_record(response.data) ? response.data : {};
    return {
      workspaceId: normalize_string(payload.workspaceId),
      languages: Array.isArray(payload.languages)
        ? payload.languages.map(normalize_language)
        : [],
      items: Array.isArray(payload.items)
        ? payload.items.map(normalize_item)
        : [],
    };
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to load translations.');
  }
}

export async function getTranslation(
  key: string,
  workspaceId: string,
  namespace: string,
  options: ApiOptions = {},
): Promise<TranslationGetResponse> {
  try {
    const response = await axios.get<unknown>(
      `/api/v1/translations/${encodeURIComponent(key)}`,
      {
        headers: build_headers(options),
        params: { workspaceId, namespace },
      },
    );
    return normalize_item(response.data);
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to load the requested translation.');
  }
}

export async function putTranslation(
  key: string,
  workspaceId: string,
  namespace: string,
  body: TranslationPutRequest,
  options: ApiOptions = {},
): Promise<TranslationPutResponse> {
  try {
    const response = await axios.put<unknown>(
      `/api/v1/translations/${encodeURIComponent(key)}`,
      {
        values: body.values,
        description: body.description,
      },
      {
        headers: build_headers(options),
        params: { workspaceId, namespace },
      },
    );
    return normalize_item(response.data);
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to save the translation.');
  }
}

export async function deleteTranslation(
  key: string,
  workspaceId: string,
  namespace: string,
  options: ApiOptions = {},
): Promise<void> {
  try {
    await axios.delete(
      `/api/v1/translations/${encodeURIComponent(key)}`,
      {
        headers: build_headers(options),
        params: { workspaceId, namespace },
      },
    );
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to delete the translation.');
  }
}

export async function importTranslations(
  workspaceId: string,
  body: TranslationImportRequest,
  options: ApiOptions = {},
): Promise<TranslationImportResponse> {
  try {
    const response = await axios.post<unknown>(
      '/api/v1/translations/import',
      body,
      {
        headers: build_headers(options),
        params: { workspaceId },
      },
    );
    const payload = is_record(response.data) ? response.data : {};
    return {
      imported: typeof payload.imported === 'number' ? payload.imported : 0,
      skipped: typeof payload.skipped === 'number' ? payload.skipped : 0,
      deleted: typeof payload.deleted === 'number' ? payload.deleted : 0,
    };
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to import translations.');
  }
}

export async function generateTranslations(
  workspaceId: string,
  body: TranslationGenerateRequest,
  options: ApiOptions = {},
): Promise<TranslationGenerateResponse> {
  try {
    const response = await axios.post<unknown>(
      '/api/v1/translations/generate',
      body,
      {
        headers: build_headers(options),
        params: { workspaceId },
      },
    );
    const payload = is_record(response.data) ? response.data : {};
    return {
      generated: typeof payload.generated === 'number' ? payload.generated : 0,
      remaining: typeof payload.remaining === 'number' ? payload.remaining : 0,
      byLocale: is_record(payload.byLocale)
        ? Object.fromEntries(
            Object.entries(payload.byLocale).filter(([, v]) => typeof v === 'number') as [string, number][],
          )
        : {},
    };
  } catch (error) {
    throw to_translations_api_error(error, 'Failed to generate translations.');
  }
}
